/**
 * Staff order API service — staff order Eduvos-branded apparel against a
 * personal voucher (October 2026).
 *
 * Two surfaces, both on the performance service:
 *   - Staff: `/api/v1/performance/staff-orders/...` — any signed-in employee.
 *     The API scopes everything to the verified caller, so there is no
 *     employee id anywhere in these calls.
 *   - HR: `/api/v1/admin/staff-orders/...` — admin / hr_admin.
 *
 * Money is integer cents throughout (R1,034.94 = 103494). Datetimes are
 * ISO-8601 with an offset; the UI shows them in South African time. Error
 * `detail` strings are written for the end user and can be shown as-is.
 */

import { apiClient, isAuthReady, waitForAuthReady } from '../apiClient'
import { API_CONFIG } from '../../config/api'
import { authService } from '../../auth/authService'

// ==================== Types: staff ====================

export type StaffOrderRoundStatus = 'draft' | 'open' | 'closed'

export interface StaffOrderSize {
  size: string
  price_cents: number
}

export interface StaffOrderItem {
  id: string
  slug: string
  /** e.g. "Performance Polo, short sleeve (Women's)" */
  name: string
  /** Supplier wording. */
  description: string | null
  /** e.g. "/assets/staff-order/polo-women.jpg" — served by the frontend. */
  image_url: string | null
  note: string | null
  /** In display order; the price can differ by size. */
  sizes: StaffOrderSize[]
  /** False when HR has switched the item off: it is listed only because it is in this person's order. */
  available?: boolean
}

export interface StaffOrderCampus {
  code: string
  label: string
}

export interface StaffOrderRound {
  id: string
  name: string
  /** HR's words, plain text with line breaks. */
  intro_text: string | null
  status: StaffOrderRoundStatus
  closes_at: string | null
  size_guide_url: string | null
  /** True when the caller may save or cancel right now. */
  can_order: boolean
  /** Why not, in plain words, when can_order is false. */
  closed_reason: string | null
}

export interface StaffOrderAllowance {
  /** The voucher. */
  amount_cents: number
  /** e.g. "Level A" — may be null; not to be made prominent. */
  tier_label: string | null
  /** A tester: the page shows a small "Test" badge. */
  is_test: boolean
}

export interface StaffOrderLine {
  item_id: string
  size: string
  quantity: number
  /** The price the line was saved at (a later price change does not touch it). */
  unit_price_cents: number
}

export interface StaffOrder {
  /** A campus code. */
  delivery_campus: string
  total_cents: number
  submitted_at: string
  updated_at: string
  lines: StaffOrderLine[]
}

/**
 * The caller's order page. `round: null` (and `allowance: null`) means there
 * is no order open for this person.
 */
export interface StaffOrderCurrent {
  round: StaffOrderRound | null
  allowance: StaffOrderAllowance | null
  items: StaffOrderItem[]
  /** Delivery campuses, in display order. */
  campuses: StaffOrderCampus[]
  order: StaffOrder | null
}

export interface StaffOrderLineInput {
  item_id: string
  size: string
  /** 1–10 per line. */
  quantity: number
}

export interface StaffOrderSave {
  delivery_campus: string
  /** At least one line. */
  lines: StaffOrderLineInput[]
}

// ==================== Types: HR ====================

export interface StaffOrderRoundSummary {
  id: string
  name: string
  status: StaffOrderRoundStatus
  closes_at: string | null
  opened_at: string | null
  closed_at: string | null
  created_at: string
  people: number
  testers: number
  invited: number
  ordered: number
  /** Real people only (testers excluded). */
  voucher_total_cents: number
  /** Real people only (testers excluded). */
  ordered_total_cents: number
  /**
   * While this is in the future, emails from the round are still going out
   * (about 25 a minute) and a new invitation/reminder send answers 409.
   */
  sending_until: string | null
}

export interface StaffOrderRoundItem extends StaffOrderItem {
  sort_order: number
  is_active: boolean
  ordered_quantity: number
}

export interface StaffOrderCampusTotal {
  code: string
  label: string
  orders: number
  ordered_total_cents: number
}

export interface StaffOrderRoundDetail extends StaffOrderRoundSummary {
  intro_text: string | null
  size_guide_url: string | null
  items: StaffOrderRoundItem[]
  by_campus: StaffOrderCampusTotal[]
  /** True outside PROD: only testers can be emailed. */
  test_sends_only: boolean
}

export interface StaffOrderRoundCreate {
  name: string
  closes_at: string | null
  intro_text?: string | null
  size_guide_url?: string | null
}

/**
 * All optional. `status: 'open'` opens ordering for everyone with a voucher
 * (409 when closes_at is unset or past); `'closed'` closes it; `'draft'` only
 * from open with no real orders yet.
 */
export interface StaffOrderRoundUpdate {
  name?: string
  intro_text?: string | null
  closes_at?: string | null
  size_guide_url?: string | null
  status?: StaffOrderRoundStatus
}

/** A price change applies to new saves only; existing orders keep their price. */
export interface StaffOrderItemUpdate {
  is_active?: boolean
  note?: string | null
  sizes?: StaffOrderSize[]
}

export type StaffOrderPeopleFilter = 'all' | 'ordered' | 'not_ordered' | 'testers'

export interface StaffOrderPerson {
  employee_id: string
  display_name: string | null
  job_title: string | null
  tier_label: string | null
  amount_cents: number
  is_test: boolean
  invited_at: string | null
  reminded_at: string | null
  ordered: boolean
  total_cents: number
  delivery_campus: string | null
  updated_at: string | null
}

export interface StaffOrderImportRowIssue {
  row: number
  employee_id: string | null
  reason: string
}

export interface StaffOrderImportResult {
  added: number
  updated: number
  removed: number
  /** Employee numbers kept on a replace because they have already ordered. */
  kept_with_orders: string[]
  /** Rows that were NOT loaded, with the reason. */
  rejected: StaffOrderImportRowIssue[]
  /**
   * Rows that WERE loaded but deserve a look, e.g. "Loaded, but not permanent
   * or fixed-term (ICA)". Optional so an older server without it still reads.
   */
  warnings?: Array<{ row: number; employee_id: string; reason: string }>
}

/** merge (default) adds and updates; replace also removes anyone not in the
 *  file, except people who have already ordered. */
export type StaffOrderImportMode = 'merge' | 'replace'

export type StaffOrderEmailKind = 'invitation' | 'reminder'

export interface StaffOrderEmailPreview {
  subject: string
  html: string
  /** How many a real send would reach now. */
  recipients: number
  /** How many a tester send would reach now. */
  testers: number
}

export interface StaffOrderQueued {
  queued: number
}

// ==================== Service ====================

const STAFF_BASE = '/api/v1/performance/staff-orders'
/** How long the banner's quiet read waits for sign-in to finish. */
const SILENT_AUTH_WAIT_MS = 15_000
const ADMIN_BASE = '/api/v1/admin/staff-orders'

const seg = (value: string) => encodeURIComponent(value)

/**
 * A failed `responseType: 'blob'` request carries its JSON error body as a
 * Blob, so `err.response.data.detail` is undefined and the user would only
 * ever see a generic message. Read the Blob back into JSON so the server's
 * own wording survives, then rethrow.
 */
async function rethrowBlobError(err: unknown): Promise<never> {
  const response = (err as { response?: { data?: unknown } } | null)?.response
  const data = response?.data
  if (response && typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      response.data = JSON.parse(await data.text())
    } catch {
      // Not JSON — leave the original error untouched.
    }
  }
  throw err
}

function filenameFrom(headers: unknown, fallback: string): string {
  const cd = (headers as Record<string, unknown> | null | undefined)?.['content-disposition']
  const m = typeof cd === 'string' ? cd.match(/filename="?([^"]+)"?/) : null
  return m?.[1] || fallback
}

export const staffOrderService = {
  // ---------- Staff ----------

  /** The caller's order page: their round, voucher, the catalogue and their order. */
  async getCurrent(): Promise<StaffOrderCurrent> {
    const response = await apiClient.get<StaffOrderCurrent>(`${STAFF_BASE}/current`)
    return response.data
  },

  /**
   * The same page, read without any of apiClient's side effects — no error
   * toasts, no token refresh, no session-expiry prompt — for the journey
   * page's banner, which must stay silent whatever goes wrong. Null on any
   * failure (no token, network, non-2xx, unreadable body). Same pattern as
   * supportService.checkUnreadSilent.
   */
  async getCurrentSilent(): Promise<StaffOrderCurrent | null> {
    try {
      // Straight after sign-in the token may not be in yet: wait for it as apiClient does, but not for long.
      if (!isAuthReady()) {
        await Promise.race([waitForAuthReady(), new Promise<void>((resolve) => setTimeout(resolve, SILENT_AUTH_WAIT_MS))])
      }
      const token = authService.getToken()
      if (!token) return null
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
      // Test personas (dev / UAT) are sent the way apiClient sends them.
      if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_USERS === 'true') {
        const stored = localStorage.getItem('dev_selected_persona')
        if (stored) {
          try {
            const persona = JSON.parse(stored)
            headers['X-Test-User-Id'] = persona.personnelNumber || 'TEST001'
            headers['X-Test-User-Roles'] = (persona.roles || ['employee']).join(',')
            if (persona.permissions?.length) headers['X-Test-User-Permissions'] = persona.permissions.join(',')
          } catch {
            // An unreadable persona: send the request as the signed-in person.
          }
        }
      }
      const response = await fetch(`${API_CONFIG.baseURL || ''}${STAFF_BASE}/current`, { headers })
      if (!response.ok) return null
      return (await response.json()) as StaffOrderCurrent
    } catch {
      return null
    }
  },

  /** Replace the caller's whole order. Returns the refreshed order page. */
  async saveMyOrder(roundId: string, body: StaffOrderSave): Promise<StaffOrderCurrent> {
    const response = await apiClient.put<StaffOrderCurrent>(
      `${STAFF_BASE}/${seg(roundId)}/my-order`,
      body,
    )
    return response.data
  },

  /** Cancel the caller's order while ordering is open. Returns the refreshed order page. */
  async cancelMyOrder(roundId: string): Promise<StaffOrderCurrent> {
    const response = await apiClient.delete<StaffOrderCurrent>(
      `${STAFF_BASE}/${seg(roundId)}/my-order`,
    )
    return response.data
  },

  // ---------- HR: rounds ----------

  async listRounds(): Promise<StaffOrderRoundSummary[]> {
    const response = await apiClient.get<StaffOrderRoundSummary[]>(`${ADMIN_BASE}/rounds`)
    return response.data
  },

  async createRound(body: StaffOrderRoundCreate): Promise<StaffOrderRoundDetail> {
    const response = await apiClient.post<StaffOrderRoundDetail>(`${ADMIN_BASE}/rounds`, body)
    return response.data
  },

  async getRound(roundId: string): Promise<StaffOrderRoundDetail> {
    const response = await apiClient.get<StaffOrderRoundDetail>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}`,
    )
    return response.data
  },

  async updateRound(roundId: string, body: StaffOrderRoundUpdate): Promise<StaffOrderRoundDetail> {
    const response = await apiClient.patch<StaffOrderRoundDetail>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}`,
      body,
    )
    return response.data
  },

  async updateItem(
    roundId: string,
    itemId: string,
    body: StaffOrderItemUpdate,
  ): Promise<StaffOrderRoundDetail> {
    const response = await apiClient.patch<StaffOrderRoundDetail>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/items/${seg(itemId)}`,
      body,
    )
    return response.data
  },

  // ---------- HR: people with a voucher ----------

  async listPeople(
    roundId: string,
    filter: StaffOrderPeopleFilter = 'all',
  ): Promise<StaffOrderPerson[]> {
    const response = await apiClient.get<StaffOrderPerson[]>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/people`,
      { params: { filter } },
    )
    return response.data
  },

  /**
   * Upload the voucher list (.xlsx or .csv). Columns: Employee number,
   * Amount (Rands), optional Level, optional Test (Y/N).
   */
  async importPeople(
    roundId: string,
    file: File,
    mode: StaffOrderImportMode = 'merge',
  ): Promise<StaffOrderImportResult> {
    const fd = new FormData()
    fd.append('file', file)
    const response = await apiClient.post<StaffOrderImportResult>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/people/import`,
      fd,
      { params: { mode }, headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  },

  /** Add testers (is_test = true), each with the same voucher and category (e.g. "Category A"). */
  async addTesters(
    roundId: string,
    employeeIds: string[],
    amountCents: number,
    tierLabel: string | null = null,
  ): Promise<StaffOrderImportResult> {
    const response = await apiClient.post<StaffOrderImportResult>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/testers`,
      { employee_ids: employeeIds, amount_cents: amountCents, tier_label: tierLabel },
    )
    return response.data
  },

  /** Remove a voucher holder who has not ordered (409 if they have). */
  async removePerson(roundId: string, employeeId: string): Promise<void> {
    await apiClient.delete(`${ADMIN_BASE}/rounds/${seg(roundId)}/people/${seg(employeeId)}`)
  },

  // ---------- HR: emails ----------

  /** Email everyone not yet invited (testers only, or everyone). Sent in the background. */
  async sendInvitations(roundId: string, testersOnly: boolean): Promise<StaffOrderQueued> {
    const response = await apiClient.post<StaffOrderQueued>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/invitations`,
      { testers_only: testersOnly },
    )
    return response.data
  },

  /** Email invited people who have not ordered. Sent in the background. */
  async sendReminders(roundId: string, testersOnly: boolean): Promise<StaffOrderQueued> {
    const response = await apiClient.post<StaffOrderQueued>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/reminders`,
      { testers_only: testersOnly },
    )
    return response.data
  },

  /** The email as that send would go out now (testers get the test version), and how many a
   *  real / tester send would reach. */
  async getEmailPreview(
    roundId: string,
    kind: StaffOrderEmailKind,
    testersOnly = false,
  ): Promise<StaffOrderEmailPreview> {
    const response = await apiClient.get<StaffOrderEmailPreview>(
      `${ADMIN_BASE}/rounds/${seg(roundId)}/email-preview`,
      { params: { kind, testers_only: testersOnly } },
    )
    return response.data
  },

  // ---------- HR: export ----------

  /**
   * The orders workbook: Summary (item × size × campus), By campus, Orders by
   * person, Not ordered, Campus check. `procurement` is Procurement's copy:
   * Summary and By campus only, without job titles, levels, vouchers or seats.
   */
  async exportOrders(
    roundId: string,
    opts?: { includeTesters?: boolean; procurement?: boolean },
  ): Promise<{ blob: Blob; filename: string }> {
    const stamp = new Date().toISOString().slice(0, 10)
    try {
      const response = await apiClient.get(`${ADMIN_BASE}/rounds/${seg(roundId)}/export`, {
        params: {
          include_testers: opts?.includeTesters ? 'true' : 'false',
          view: opts?.procurement ? 'procurement' : 'full',
        },
        responseType: 'blob',
      })
      return {
        blob: response.data as Blob,
        filename: filenameFrom(response.headers, `staff-order-${stamp}.xlsx`),
      }
    } catch (err) {
      return rethrowBlobError(err)
    }
  },
}

export default staffOrderService
